import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { CompleteProfileFormInputs, CompleteProfileFormSchema } from "./validators/auth/CompleteProfileFormSchema"
import { User } from "@/app/generated/prisma"

export function cn(...inputs: ClassValue[]) {
   return twMerge(clsx(inputs))
}

export function getGteDate(units: number, group: string): Date {
   const now = new Date()

   switch (group) {
      case "week":
         return new Date(now.getTime() - units * 7 * 24 * 60 * 60 * 1000)
      case "month":
         return new Date(now.getTime() - units * 30 * 24 * 60 * 60 * 1000)
      default:
         return new Date(now.getTime() - units * 24 * 60 * 60 * 1000)
   }
}

export function isCloudFrontUrl(url: string | undefined | null) {
   if (!url) return false
   const cloudFrontPattern = /cloudfront\.net/
   return cloudFrontPattern.test(url)
}

// Static, developer-curated icon/illustration folders: flat-color file-type
// icons and small graphics with sharp edges/text that visibly band or blur
// under lossy WebP/AVIF re-encoding. Content uploaded through the CMS
// (authors, articles, library covers, etc.) is photographic and is not
// listed here, so it still gets optimized even when saved as PNG.
const LOSSLESS_GRAPHIC_PATH_MARKERS = ["/_images/ui/", "/_images/resources/", "/_images/benefits/"]

/**
 * SVG/GIF are always lossless-skipped: SVG is vector (rasterizing it is
 * strictly worse) and GIF is often animated (the optimizer would flatten it
 * to a single frame). PNG is only skipped when it lives in one of the known
 * static icon/illustration folders above — everywhere else (author photos,
 * article images, etc.) PNG is treated like any other raster photo and goes
 * through the Next.js image optimizer for resizing and AVIF/WebP re-encoding.
 */
export function isLosslessGraphicUrl(url: string | undefined | null): boolean {
   if (!url) return false
   const path = url.split(/[?#]/)[0]
   if (/\.(svg|gif)$/i.test(path)) return true
   if (/\.png$/i.test(path)) {
      const lowerPath = path.toLowerCase()
      return LOSSLESS_GRAPHIC_PATH_MARKERS.some((marker) => lowerPath.includes(marker))
   }
   return false
}

/**
 * Constrains the size of remote images that we hotlink (currently Unsplash).
 * Unsplash serves through imgix, so appending `w`/`q`/`fm`/`fit` returns a
 * resized, re-encoded variant instead of the multi-MB original. Existing query
 * params (e.g. `ixid`, `ixlib`) are preserved. Non-Unsplash URLs are returned
 * unchanged. This composes with the Next.js image optimizer, which fetches this
 * (already smaller) source and resizes it further per the request `sizes`.
 */
export function optimizeRemoteImageUrl(url: string | undefined | null, maxWidth = 1920): string {
   if (!url) return ""
   try {
      const parsed = new URL(url)
      if (!parsed.hostname.endsWith("unsplash.com")) return url
      parsed.searchParams.set("w", String(maxWidth))
      parsed.searchParams.set("q", "70")
      parsed.searchParams.set("fm", "webp")
      parsed.searchParams.set("fit", "max")
      return parsed.toString()
   } catch {
      return url
   }
}

export function generateSlug(str: any): string {
   let slug = str
      .toLowerCase() // Convert to lowercase first
      .replace(/\s+/g, "-") // Replace all whitespace (including multiple spaces) with a single hyphen
      .replace(/[^a-z0-9-]/g, "") // Remove all non-alphanumeric and non-hyphen characters
      .replace(/-+/g, "-") // Collapse multiple hyphens into a single hyphen
      .replace(/^-+|-+$/g, "")
   return slug.toLowerCase()
}

export function formatImageUrl(url: string): URL {
   if (!url.startsWith("http")) {
      if (url.startsWith("/")) {
         url = `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/_images/articles${url}`
      } else {
         url = `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/_images/articles/${url}`
      }
   }
   return new URL(
      url
         .replace(/&/g, "&amp;")
         .replace(/</g, "&lt;")
         .replace(/>/g, "&gt;")
         .replace(/"/g, "&quot;")
         .replace(/'/g, "&apos;"),
   )
}

export function specialCategoryNaming(input: string) {
   switch (input) {
      case 'operations-blog':
         return "Operations"
         break;

      default:
         break;
   }
}

/**
 * @deprecated A category's display name now lives in `Category.title`. Render
 * {@link getCategoryTitle} instead of re-deriving a label from `Category.name`,
 * which is the slug/path source. Retained only as the fallback inside
 * `getCategoryTitle` for rows whose `title` has not been backfilled yet.
 */
export function deSlugify(input: string): string {

   const specialCase = specialCategoryNaming(input)
   if (specialCase) {
      return specialCase
   }

   return input
      .replace(/[-_]/g, " ") // Replace hyphens and underscores with spaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
}

/**
 * The single place that decides how a category is labelled in the UI.
 *
 * `Category.title` is the editorial display name and the only value that should
 * be rendered. `Category.name` stays the slug/path source (see `generateSlug`)
 * and is humanized only as a fallback for rows that have no `title` yet.
 */
export function getCategoryTitle(category: { title?: string | null; name?: string | null } | null | undefined): string {
   const title = category?.title?.trim()
   if (title) return title

   const name = category?.name?.trim()
   return name ? deSlugify(name) : ""
}

export function isProfileCompleteStrict(user: Omit<User, "password"> | null | undefined): boolean {
   if (!user) return false
   const data: CompleteProfileFormInputs = {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      company: user.company || "",
      companyUrl: user.website || "",
      phone: user.phone || "",
      position: user.title || "",
      city: user.city || "",
      stateProvince: user.state || "",
      companyOperations: user.companyOperations || "",
      companyDescription: user.companyDescription || "",
   }
   // Leverage the same schema used by Complete Profile form for a high‑quality check
   return CompleteProfileFormSchema.safeParse(data).success
}
